import gql from 'graphql-tag';

export type RewardsSummary = {
  __typename: 'RestRewardsSummary';
  id: string;
  attributes: {
    __typename: 'RestRewardsSummaryAttributes';
    userId: number;
    currencyUnit: string;
    totalRewardPoints: number;
    loyaltyRewardPromotionId: number;
    expiresOn: string;
    createdAt: string;
    updatedAt: string;
    getRewardsData: GetRewardsData;
    getRewardsSummary: GetRewardsSummary;
  };
};

export type GetRewardsData = {
  __typename: 'RestGetRewardsData';
  faqLink: string;
  userEligibleForRewards: boolean;
  useLoyaltyRewards: boolean;
  totalRewardsPoints: number;
  rewardPointsAmount: number;
  redeemablePoints: number;
  redeemableAmount: string;
  rewardsExpired: boolean;
  rewardsExpiring: boolean;
  pointsRequired: number;
  rewardsExpiresIn: string | null;
  preferredCurrency: string;
  displayRedeemBtn: boolean;
  isGuestUser: boolean;
  redeemedRewardPoints: number;
  currentPoints: string;
  isAdmin: boolean;
  enrolledForLoyalty: boolean;
  currencyHtmlCode: string;
  hasMulticurrencyRewards: boolean;
  multiCurrencies: [string];
  userCurrenciesSymbol: Record<string, string>;
  totalRewardsPointsWithDelimeter: string;
  rewardsCurrencyData: RewardsCurrencyData;
};

export type RewardsCurrencyData = {
  __typename: 'RestRewardsCurrencyData';
  currencySymbol: string;
  thresholdAmount: number;
  thresholdPoint: string;
  currencyUnit: string;
};

export type GetRewardsSummary = {
  __typename: 'RestGetRewardsSummary';
  summary: [GetRewardsSummarySummary];
  admin: boolean;
  totalRecordCount: number;
  expiresOn: string;
  rewardsExpiring: boolean;
  rewardsExpired: false;
  recordsCount: number;
  currencyUnit: string;
  page: number;
  url: string;
  userId: number;
};

export type GetRewardsSummarySummary = {
  __typename: 'RestGetRewardsSummarySummary';
  id: string;
  date: string;
  reason: string;
  qualifyingSpent: string;
  earnedPoints: string;
  totalPoints: string;
  editedBy: string;
  pendingForApproval: number;
};

export type GetRewardsSummaryQueryVariables = {
  countryCode: string;
  currency?: string;
};

export type GetRewardsSummaryQuery = {
  getRewardsSummary: {
    __typename: 'RestRewardsSummary';
    data: RewardsSummary[];
  };
};

export const REWARDS_SUMMARY_FRAGMENT = gql`
  fragment RewardsSummaryFragment on RestRewardsSummary {
    id
    attributes @type(name: "RestRewardsSummaryAttributes") {
      userId: user_id
      currencyUnit: currency_unit
      totalRewardPoints: total_reward_points
      loyaltyRewardPromotionId: loyalty_reward_promotion_id
      expiresOn: expires_on
      createdAt: created_at
      updatedAt: updated_at
      getRewardsData: get_rewards_data @type(name: "RestGetRewardsData") {
        userEligibleForRewards: user_eligible_for_rewards
        useLoyaltyRewards: use_loyalty_rewards
        totalRewardsPoints: total_rewards_points
        rewardPointsAmount: reward_points_amount
        redeemablePoints: redeemable_points
        redeemableAmount: redeemable_amount
        rewardsExpired: rewards_expired
        rewardsExpiring: rewards_expiring
        pointsRequired: points_required
        rewardsExpiresIn: rewards_expires_in
        preferredCurrency: preferred_currency
        displayRedeemBtn: display_redeem_btn
        isGuestUser: is_guest_user
        redeemedRewardPoints: redeemed_reward_points
        currentPoints: current_points
        isAdmin: is_admin
        enrolledForLoyalty: enrolled_for_loyalty
        currencyHtmlCode: currency_html_code
        hasMulticurrencyRewards: has_multicurrency_rewards
        multiCurrencies: multi_currencies
        userCurrenciesSymbol: user_currencies_symbol
        totalRewardsPointsWithDelimeter: total_rewards_points_with_delimeter
        rewardsCurrencyData: rewards_currency_data @type(name: "RestRewardsCurrencyData") {
          currencySymbol: currency_symbol
          currencyUnit: currency_unit
          thresholdAmount: threshold_amount
          thresholdPoint: threshold_point
        }
      }
      getRewardsSummary: get_rewards_summary @type(name: "RestGetRewardsSummary") {
        summary: summary @type(name: "GetRewardsSummarySummary") {
          date: date
          reason: reason
          qualifyingSpent: qualifying_spent
          earnedPoints: earned_points
          totalPoints: total_points
          editedBy: edited_by
          pendingForApproval: pending_for_approval
        }
        admin: admin
        totalRecordCount: total_record_count
        expiresOn: expires_on
        rewardsExpiring: rewards_expiring
        rewardsExpired: rewards_expired
        recordsCount: records_count
        currencyUnit: currency_unit
      }
    }
  }
`;

export const GET_REWARDS_SUMMARY_QUERY = gql`
  query GetRewardsSummaryQuery($countryCode: String!, $currency: String) {
    getRewardsSummary(country_code: $countryCode, selected_currency: $currency)
      @rest(
        type: "RestRewardsSummary"
        endpoint: "user_loyalty_rewards_summaries"
        path: "/v3.5/user_loyalty_rewards_summaries?{args}&page[size]=1000&page[number]=1"
      ) {
      data @type(name: "RestRewardsSummary") {
        ...RewardsSummaryFragment
      }
    }
  }
  ${REWARDS_SUMMARY_FRAGMENT}
`;
